import React from 'react';
import { TERMINAL_COLORS } from '../constants/colorConstants';
import { TerminalRenderers } from '../components/terminals';

/**
 * Renderizza un'icona di terminale semplice (senza container)
 * @param {string} cod - Codice del terminale
 * @param {number} width - Larghezza in pixel (default: 14)
 * @param {number} height - Altezza in pixel (default: 7)
 * @returns {JSX.Element|null} - Elemento SVG o null se non supportato
 */
export const renderTerminalIconSimple = (cod, width = 14, height = 7, terminalData = null) => {
  if (cod === 'SPS') return null; // Nessuna icona per SPS

  // Scala basata sulle dimensioni
  const scale = Math.min(width / 210, height / 70);
  const viewBoxWidth = width / scale;
  const viewBoxHeight = height / scale;
  const halfWidth = viewBoxWidth / 2;
  const halfHeight = viewBoxHeight / 2;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`${-halfWidth} ${-halfHeight} ${viewBoxWidth} ${viewBoxHeight}`}
      className="w-full h-full"
    >
      {renderTerminal(cod, 0, 0, scale, 'A', 0, terminalData)}
    </svg>
  );
};

/**
 * Renderizza un terminale per uso schematico (all'interno di SVG)
 * @param {string} cod - Codice del terminale
 * @param {number} x - Coordinata X
 * @param {number} y - Coordinata Y
 * @param {number} scale - Fattore di scala (default: 0.15)
 * @param {string} side - Lato ('A' o 'B', default: 'A')
 * @param {number} rotate - Rotazione in gradi (default: 0)
 * @returns {JSX.Element|null} - Elemento g o null se non supportato
 */
export const renderTerminalSchematic = (cod, x, y, scale = 0.15, side = 'A', rotate = 0, terminalData = null) => {
  if (cod === 'SPS') return null; // Nessuna icona per SPS
  return renderTerminal(cod, x, y, scale, side, rotate, terminalData);
};

export const renderTerminal = (cod, x, y, scale = 0.08, side = 'A', rotate = 0, terminalData = null) => {
  if (cod === 'NS') return null;
  if (cod && cod.startsWith('SP') && cod.length > 2) return null; // SPx è una spellatura, non un terminale grafico

  let rendererKey = null;
  const iconVariant = terminalData?.[cod]?.iconVariant || 'default';

  if (cod && cod.startsWith('PS')) rendererKey = 'PS';
  else if (cod && cod.startsWith('PD')) rendererKey = 'PD';
  else if (cod && cod.startsWith('FR')) rendererKey = 'FR';
  else if (cod && cod.startsWith('FB')) rendererKey = 'FR'; // Usa lo stesso renderer per FB
  else if (cod && cod.startsWith('FY')) rendererKey = 'FR'; // Usa lo stesso renderer per FY
  else if (cod && cod.startsWith('OR')) rendererKey = 'OR';
  else if (cod && cod.startsWith('OB')) rendererKey = 'OR'; // Usa lo stesso renderer per OB
  else if (cod && cod.startsWith('OY')) rendererKey = 'OR'; // Usa lo stesso renderer per OY
  else if (cod === 'MLM') rendererKey = 'MLM';
  else if (cod === 'MLF' || cod === 'MFF') rendererKey = 'MLF';
  else if (cod && cod.startsWith('T')) rendererKey = 'TR';
  else if (cod === 'FSR2.8') rendererKey = 'FSR2_8';
  else if (cod === 'FSR4.8') rendererKey = 'FSR4_8';
  else if (cod === 'FSR6.3') rendererKey = 'FSR6_3';
  else if (cod === 'FCR6.3') rendererKey = 'FC6_3';
  else if (cod === 'FCB6.3') rendererKey = iconVariant === 'alt' ? 'FC6_3_ALT' : 'FC6_3';
  else if (cod === 'FS6.3') rendererKey = 'FSR6_3_ALT';
  else if (cod === 'FC6.3') rendererKey = 'FC6_3_ALT';
  else if (cod && cod.startsWith('FC') && cod.endsWith('2.8')) rendererKey = 'FC2_8';
  else if (cod && cod.startsWith('FC') && cod.endsWith('4.8')) rendererKey = 'FC4_8';
  else if (cod === 'FAS') rendererKey = 'FAS';

  const TerminalComponent = TerminalRenderers[rendererKey];

  if (TerminalComponent) {
    let terminalColor = TERMINAL_COLORS[cod] || TERMINAL_COLORS[rendererKey];

    // Gestione colori specifici per forcine e occhielli (FRx, ORx)
    if (cod && cod.startsWith('FR')) terminalColor = TERMINAL_COLORS['FRx'];
    else if (cod && cod.startsWith('FB')) terminalColor = TERMINAL_COLORS['FBx'];
    else if (cod && cod.startsWith('FY')) terminalColor = TERMINAL_COLORS['FYx'];
    else if (cod && cod.startsWith('OR')) terminalColor = TERMINAL_COLORS['ORx'];
    else if (cod && cod.startsWith('OB')) terminalColor = TERMINAL_COLORS['OBx'];
    else if (cod && cod.startsWith('OY')) terminalColor = TERMINAL_COLORS['OYx'];

    // Gestione colori per famiglia FCx (faston coperti)
    else if (cod && cod.startsWith('FC')) terminalColor = TERMINAL_COLORS[cod];

    return TerminalComponent({ color: terminalColor, x, y, scale, side, rotate });
  }

  // Default: cerchio blu se non c'è un renderer specifico
  return (
    <circle
      className="terminal-circle"
      cx={x}
      cy={y}
      r={12}
      fill="#3B82F6"
      stroke="#1D4ED8"
      strokeWidth={2}
    />
  );
};

